/**
 * Les flèches du plateau (C28 SP4, spec § 4) — module PUR, sans DOM.
 *
 * Une flèche est un trait de lecture (D3) : elle aide à voir les éléments entre eux et ne devient rien dans la
 * note. Libre au tracé, accrochée à l'arrivée (D4) : une extrémité relâchée sur une carte ou un cadre le suit ;
 * sinon elle reste un point du plateau. La géométrie lit des rectangles mesurés par la zone (`Rectangles`) ;
 * sans mesure, une carte vaut sa taille nominale (`CARTE_TAILLE`).
 */
import type { Carte, Extremite, Fleche, Ilot } from '../../types/metanote';
import { METANOTE_LABELS as L } from '../../components/metanotes/metanoteLabels';
import { CARTE_TAILLE, contient, type Point } from './appartenance';
import type { Cadre } from './ilots';
import { nouvelId } from './id';

export interface Rectangle { x: number; y: number; largeur: number; hauteur: number }
/** Rectangles par identifiant de carte et de cadre ; une carte absente vaut `CARTE_TAILLE`. */
export type Rectangles = ReadonlyMap<string, Rectangle>;

/** En deçà, un tracé est un clic : aucune flèche. */
export const FLECHE_MIN = 24;

export const estAccrochee = (e: Extremite): e is { carteId: string } | { ilotId: string } => !('x' in e);

export function rectanglesNominaux(cartes: readonly Carte[], ilots: readonly Ilot[]): Map<string, Rectangle> {
  const out = new Map<string, Rectangle>();
  for (const c of cartes) out.set(c.id, { x: c.x, y: c.y, largeur: CARTE_TAILLE.largeur, hauteur: CARTE_TAILLE.hauteur });
  for (const i of ilots) out.set(i.id, { x: i.x, y: i.y, largeur: i.largeur, hauteur: i.hauteur });
  return out;
}

/** Le rectangle d'un élément : la mesure si elle existe, sinon la taille nominale d'une carte ou celle du cadre ; null si inconnu. */
export function rectangleDe(id: string, cartes: readonly Carte[], ilots: readonly Ilot[], rectangles: Rectangles): Rectangle | null {
  const carte = cartes.find(c => c.id === id);
  if (carte) {
    const mesure = rectangles.get(id);
    return { x: carte.x, y: carte.y, largeur: mesure?.largeur ?? CARTE_TAILLE.largeur, hauteur: mesure?.hauteur ?? CARTE_TAILLE.hauteur };
  }
  const ilot = ilots.find(i => i.id === id);
  return ilot ? { x: ilot.x, y: ilot.y, largeur: ilot.largeur, hauteur: ilot.hauteur } : null;
}

const aire = (r: Rectangle): number => r.largeur * r.hauteur;

/** La carte sous le point (la dernière du tableau, peinte dessus), sinon le plus petit cadre, sinon le point lui-même. */
export function elementSous(point: Point, cartes: readonly Carte[], ilots: readonly Ilot[], rectangles: Rectangles): Extremite {
  for (let i = cartes.length - 1; i >= 0; i--) {
    const r = rectangleDe(cartes[i].id, cartes, ilots, rectangles);
    if (r && contient(r, point)) return { carteId: cartes[i].id };
  }
  let gagnant: Ilot | null = null;
  for (const ilot of ilots) {
    if (contient(ilot, point) && (!gagnant || aire(ilot) < aire(gagnant))) gagnant = ilot;
  }
  return gagnant ? { ilotId: gagnant.id } : { x: point.x, y: point.y };
}

const idDe = (e: Extremite): string | null => ('carteId' in e ? e.carteId : 'ilotId' in e ? e.ilotId : null);
const centre = (r: Rectangle): Point => ({ x: r.x + r.largeur / 2, y: r.y + r.hauteur / 2 });

/** Le point du bord de `r` sur la droite qui va de son centre vers `cible` ; le centre si les deux coïncident. */
function bord(r: Rectangle, cible: Point): Point {
  const c = centre(r);
  const dx = cible.x - c.x;
  const dy = cible.y - c.y;
  if (dx === 0 && dy === 0) return c;
  const demiL = r.largeur / 2;
  const demiH = r.hauteur / 2;
  const t = Math.min(dx === 0 ? Infinity : demiL / Math.abs(dx), dy === 0 ? Infinity : demiH / Math.abs(dy));
  return { x: Math.round(c.x + dx * t), y: Math.round(c.y + dy * t) };
}

/** Les deux points du trait ; null si une extrémité vise un élément absent. */
export function pointsDeLaFleche(f: Fleche, cartes: readonly Carte[], ilots: readonly Ilot[], rectangles: Rectangles): { de: Point; vers: Point } | null {
  const rDe = estAccrochee(f.de) ? rectangleDe(idDe(f.de)!, cartes, ilots, rectangles) : null;
  const rVers = estAccrochee(f.vers) ? rectangleDe(idDe(f.vers)!, cartes, ilots, rectangles) : null;
  if ((estAccrochee(f.de) && !rDe) || (estAccrochee(f.vers) && !rVers)) return null;
  const cDe = rDe ? centre(rDe) : (f.de as Point);
  const cVers = rVers ? centre(rVers) : (f.vers as Point);
  return {
    de: rDe ? bord(rDe, cVers) : { x: cDe.x, y: cDe.y },
    vers: rVers ? bord(rVers, cDe) : { x: cVers.x, y: cVers.y },
  };
}

/** Deux extrémités qui désignent le même élément : la flèche ne dit rien. */
const memeElement = (a: Extremite, b: Extremite): boolean => idDe(a) !== null && idDe(a) === idDe(b);

export function creerFleche(de: Point, vers: Point, cartes: readonly Carte[], ilots: readonly Ilot[], rectangles: Rectangles): Fleche | null {
  if (Math.hypot(vers.x - de.x, vers.y - de.y) < FLECHE_MIN) return null;
  const a = elementSous(de, cartes, ilots, rectangles);
  const b = elementSous(vers, cartes, ilots, rectangles);
  if (memeElement(a, b)) return null;
  return { id: nouvelId(), de: a, vers: b };
}

export function reprendreExtremite(fleches: readonly Fleche[], id: string, bout: 'de' | 'vers', point: Point, cartes: readonly Carte[], ilots: readonly Ilot[], rectangles: Rectangles): Fleche[] {
  const f = fleches.find(x => x.id === id);
  if (!f) return [...fleches];
  const nouvelle = elementSous(point, cartes, ilots, rectangles);
  const autre = bout === 'de' ? f.vers : f.de;
  if (memeElement(nouvelle, autre)) return [...fleches];
  return fleches.map(x => (x.id === id ? { ...x, [bout]: nouvelle } : x));
}

export function etiqueter(fleches: readonly Fleche[], id: string, etiquette: string): Fleche[] {
  const propre = etiquette.trim();
  return fleches.map(f => {
    if (f.id !== id) return f;
    const { etiquette: _ancienne, ...sans } = f;
    return propre ? { ...sans, etiquette: propre } : sans;
  });
}

/** Les extrémités accrochées à l'un des éléments retirés deviennent libres au dernier point calculé ; la flèche reste. */
export function detacherDes(fleches: readonly Fleche[], retires: readonly string[], cartes: readonly Carte[], ilots: readonly Ilot[], rectangles: Rectangles): Fleche[] {
  const partis = new Set(retires);
  const touche = (e: Extremite) => { const id = idDe(e); return id !== null && partis.has(id); };
  if (!fleches.some(f => touche(f.de) || touche(f.vers))) return fleches as Fleche[];
  return fleches.map(f => {
    if (!touche(f.de) && !touche(f.vers)) return f;
    const points = pointsDeLaFleche(f, cartes, ilots, rectangles);
    if (!points) return f;
    return { ...f, de: touche(f.de) ? points.de : f.de, vers: touche(f.vers) ? points.vers : f.vers };
  });
}

/** Les flèches dont les deux points calculés sont dans le rectangle (tracé dans n'importe quel sens). */
export function flechesDansLeRectangle(fleches: readonly Fleche[], rectangle: Cadre, cartes: readonly Carte[], ilots: readonly Ilot[], rectangles: Rectangles): string[] {
  const r = { x: Math.min(rectangle.x, rectangle.x + rectangle.largeur), y: Math.min(rectangle.y, rectangle.y + rectangle.hauteur), largeur: Math.abs(rectangle.largeur), hauteur: Math.abs(rectangle.hauteur) };
  return fleches.filter(f => {
    const p = pointsDeLaFleche(f, cartes, ilots, rectangles);
    return !!p && contient(r, p.de) && contient(r, p.vers);
  }).map(f => f.id);
}

/** Déplace les extrémités libres des flèches désignées ; les accrochées suivent leur élément d'elles-mêmes. */
export function deplacerFleches(fleches: readonly Fleche[], ids: ReadonlySet<string>, dx: number, dy: number): Fleche[] {
  const bouger = (e: Extremite): Extremite => (estAccrochee(e) ? e : { x: e.x + dx, y: e.y + dy });
  return fleches.map(f => (ids.has(f.id) ? { ...f, de: bouger(f.de), vers: bouger(f.vers) } : f));
}

/**
 * Les flèches dont aucune extrémité accrochée ne vise un élément absent (choix C11 de la spec) : le serveur les écarte
 * déjà, le client les écarte aussi à la lecture — une pendante envoyée au `PUT` ferait refuser le plateau entier.
 */
export function flechesTenues(fleches: readonly Fleche[] | undefined, cartes: readonly Carte[], ilots: readonly Ilot[]): Fleche[] {
  if (!Array.isArray(fleches)) return [];
  const cartesIds = new Set(cartes.map(c => c.id));
  const ilotsIds = new Set(ilots.map(i => i.id));
  const tient = (e: Extremite) => ('carteId' in e ? cartesIds.has(e.carteId) : 'ilotId' in e ? ilotsIds.has(e.ilotId) : true);
  return fleches.filter(f => tient(f.de) && tient(f.vers));
}

/** « Texte « liberté » », « cadre « Partie 1 » », « un point du plateau » — pour l'étiquette lue par le lecteur d'écran. */
export function libelleDe(e: Extremite, cartes: readonly Carte[], ilots: readonly Ilot[]): string {
  if ('carteId' in e) {
    const c = cartes.find(x => x.id === e.carteId);
    if (!c) return L.fleche.point;
    const texte = c.sorte === 'mot' ? c.mot?.texte : c.sorte === 'concept' ? c.concept?.cle : c.sorte === 'passage' ? c.passage?.cle : c.sorte === 'note' ? c.note?.titre : L.carte.reponse;
    return L.fleche.carte(L.carte.sorte[c.sorte], (texte ?? '').slice(0, 40));
  }
  if ('ilotId' in e) {
    const i = ilots.find(x => x.id === e.ilotId);
    return i ? L.fleche.cadre(i.nom.trim()) : L.fleche.point;
  }
  return L.fleche.point;
}
